import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  HiPencil, 
  HiUserPlus, 
  HiBell,
  HiBolt,
  HiHandRaised,
  HiTrash,
  HiEye,
  HiArrowRightOnRectangle,
  HiChevronUp,
  HiChevronDown,
  HiChatBubbleLeftEllipsis,
  HiMiniArrowUturnLeft,
  HiArrowUturnUp,
  HiPaperClip,
  HiCheck,
  HiXMark,
  HiUserMinus,
  HiLink
} from 'react-icons/hi2';
import { MdOutlineAssignmentReturn } from "react-icons/md";
import { TbUserDown, TbStatusChange } from 'react-icons/tb';
import { IoCheckmarkSharp, IoCheckmarkDoneSharp } from 'react-icons/io5';
import { useModalManager } from '../Modal/ModalManager';
import { GlassHoverEffect } from '../GlassHoverEffect';

interface FloatingToolbarProps {
  // Ações customizáveis
  actions?: ToolbarAction[];
  
  // Ações padrão (retrocompatibilidade)
  onEdit?: (() => void) | undefined;
  onReturn?: (() => void) | undefined;
  onResolve?: (() => void) | undefined;
  onAssignToSelf?: (() => void) | undefined;
  onSendReminder?: (() => void) | undefined;
  
  // Novas ações para demandas
  onAssignTo?: (() => void) | undefined;
  onDeliver?: (() => void) | undefined;
  onUndoDelivery?: (() => void) | undefined;
  onAccept?: (() => void) | undefined;
  onAcceptDelivery?: (() => void) | undefined;
  onReject?: (() => void) | undefined;
  onDelete?: (() => void) | undefined;
  onAddObservers?: (() => void) | undefined;
  onTransferSector?: (() => void) | undefined;
  onWrongDemand?: (() => void) | undefined;
  onresend?: (() => void) | undefined;
  onComment?: (() => void) | undefined;
  onAttach?: (() => void) | undefined;
  onRemoveAssigned?: (() => void) | undefined;
  // Nova ação: Demanda Relacionada (default: navegar para criação)
  onRelatedDemand?: (() => void) | undefined;
  
  // Ações de Autorização
  onApprove?: (() => void) | undefined;
  onReprove?: (() => void) | undefined;
  
  // Configurações de comportamento
  autoCollapse?: boolean;
  autoCollapseDelay?: number;
  scrollCollapse?: boolean;
  closeOnAction?: boolean;
  
  // Configurações de aparência
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  size?: 'sm' | 'md' | 'lg';
  
  // Configurações de contexto
  context?: 'demand' | 'user' | 'admin' | 'general';
  title?: string;
  
  // Configuração de posicionamento
  relative?: boolean;
  
  
  className?: string;
}

interface ToolbarAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  onClick?: (() => void) | undefined;
  color: string;
  hoverColor: string;
  hidden?: boolean;
  disabled?: boolean;
  shortcut?: string; // Atalho de teclado (ex: "Ctrl+B → P")
}

// Tipo para contextos predefinidos
type ContextActionsType = {
  [K in 'demand' | 'user' | 'admin' | 'general']: (props: FloatingToolbarProps) => ToolbarAction[];
};

// Contextos predefinidos
const contextActions: ContextActionsType = {
  demand: (props: FloatingToolbarProps) => [
    {
      id: 'assignToSelf',
      label: 'Puxar para mim',
      icon: <TbUserDown className="w-5 h-5" />,
      onClick: props.onAssignToSelf,
      color: 'text-indigo-600',
      hoverColor: 'hover:bg-indigo-50 hover:text-indigo-700',
      hidden: !props.onAssignToSelf,
      shortcut: 'Ctrl+B → P'
    },
    {
      id: 'edit',
      label: 'Editar',
      icon: <HiPencil className="w-5 h-5" />,
      onClick: props.onEdit,
      color: 'text-blue-600',
      hoverColor: 'hover:bg-blue-50 hover:text-blue-700',
      hidden: !props.onEdit,
      shortcut: 'Ctrl+B → E'
    },
    {
      id: 'relatedDemand',
      label: 'Demanda Relacionada',
      icon: <HiLink className="w-5 h-5" />,
      onClick: props.onRelatedDemand,
      color: 'text-indigo-600',
      hoverColor: 'hover:bg-indigo-50 hover:text-indigo-700',
      hidden: !props.onRelatedDemand,
      shortcut: 'Ctrl+B → L'
    },
    {
      id: 'assignTo',
      label: 'Atribuir para',
      icon: <HiUserPlus className="w-5 h-5" />,
      onClick: props.onAssignTo,
      color: 'text-purple-600',
      hoverColor: 'hover:bg-purple-50 hover:text-purple-700',
      hidden: !props.onAssignTo,
      shortcut: 'Ctrl+B → A'
    },
    {
      id: 'removeAssigned',
      label: 'Remover Atribuído',
      icon: <HiUserMinus className="w-5 h-5" />,
      onClick: props.onRemoveAssigned ? () => props.onRemoveAssigned?.() : undefined,
      color: 'text-rose-600',
      hoverColor: 'hover:bg-rose-50 hover:text-rose-700',
      hidden: !props.onRemoveAssigned,
      shortcut: 'Ctrl+B → V'
    },
    {
      id: 'deliver',
      label: 'Solucionar',
      icon: <IoCheckmarkSharp className="w-5 h-5" />,
      onClick: props.onDeliver,
      color: 'text-emerald-600',
      hoverColor: 'hover:bg-emerald-50 hover:text-emerald-700',
      hidden: !props.onDeliver,
      shortcut: 'Ctrl+B → S'
    },
    {
      id: 'accept',
      label: 'Aceitar Demanda',
      icon: <HiBolt className="w-5 h-5" />,
      onClick: props.onAccept,
      color: 'text-sky-600',
      hoverColor: 'hover:bg-sky-50 hover:text-sky-700',
      hidden: !props.onAccept,
      shortcut: 'Ctrl+B → C'
    },
    {
      id: 'undoDelivery',
      label: 'Desfazer Entrega',
      icon: <HiMiniArrowUturnLeft className="w-5 h-5" />,
      onClick: props.onUndoDelivery,
      color: 'text-amber-600',
      hoverColor: 'hover:bg-amber-50 hover:text-amber-700',
      hidden: !props.onUndoDelivery,
      shortcut: 'Ctrl+B → U'
    },
    {
      id: 'acceptDelivery',
      label: 'Aceitar Entrega',
      icon: <IoCheckmarkDoneSharp className="w-5 h-5" />,
      onClick: props.onAcceptDelivery,
      color: 'text-green-600',
      hoverColor: 'hover:bg-green-50 hover:text-green-700',
      hidden: !props.onAcceptDelivery,
      shortcut: 'Ctrl+B → F'
    },
    {
      id: 'return',
      label: 'Devolver',
      icon: <MdOutlineAssignmentReturn className="w-5 h-5" />,
      onClick: props.onReturn,
      color: 'text-orange-600',
      hoverColor: 'hover:bg-orange-50 hover:text-orange-700',
      hidden: !props.onReturn,
      shortcut: 'Ctrl+B → D'
    },
    {
      id: 'reject',
      label: 'Recusar',
      icon: <HiHandRaised className="w-5 h-5" />,
      onClick: props.onReject,
      color: 'text-red-600',
      hoverColor: 'hover:bg-red-50 hover:text-red-700',
      hidden: !props.onReject,
      shortcut: 'Ctrl+B → R'
    },
    {
      id: 'delete',
      label: 'Excluir',
      icon: <HiTrash className="w-5 h-5" />,
      onClick: props.onDelete,
      color: 'text-red-700',
      hoverColor: 'hover:bg-red-50 hover:text-red-800',
      hidden: !props.onDelete,
      shortcut: 'Ctrl+B → X'
    },
    {
      id: 'reminder',
      label: 'Enviar Lembrete',
      icon: <HiBell className="w-5 h-5" />,
      onClick: props.onSendReminder,
      color: 'text-yellow-600',
      hoverColor: 'hover:bg-yellow-50 hover:text-yellow-700',
      hidden: !props.onSendReminder
    },
    {
      id: 'addObservers',
      label: 'Gerenciar Observadores',
      icon: <HiEye className="w-5 h-5" />,
      onClick: props.onAddObservers,
      color: 'text-teal-600',
      hoverColor: 'hover:bg-teal-50 hover:text-teal-700',
      hidden: !props.onAddObservers,
      shortcut: 'Ctrl+B → O'
    },
    {
      id: 'transferSector',
      label: 'Transferir Setor',
      icon: <HiArrowRightOnRectangle className="w-5 h-5" />,
      onClick: props.onTransferSector,
      color: 'text-slate-600',
      hoverColor: 'hover:bg-slate-50 hover:text-slate-700',
      hidden: !props.onTransferSector
    },
    {
      id: 'wrongDemand',
      label: 'Demanda Errada',
      icon: <HiArrowUturnUp className="w-5 h-5" />,
      onClick: props.onWrongDemand,
      color: 'text-pink-600',
      hoverColor: 'hover:bg-pink-50 hover:text-pink-700',
      hidden: !props.onWrongDemand,
      shortcut: 'Ctrl+B → W'
    },
    {
      id: 'resend',
      label: 'Reenviar',
      icon: <TbStatusChange className="w-5 h-5" />,
      onClick: props.onresend,
      color: 'text-cyan-600',
      hoverColor: 'hover:bg-cyan-50 hover:text-cyan-700',
      hidden: !props.onresend,
      shortcut: 'Ctrl+B → N'
    },
    {
      id: 'comment',
      label: 'Comentar',
      icon: <HiChatBubbleLeftEllipsis className="w-5 h-5" />,
      onClick: props.onComment,
      color: 'text-cyan-600',
      hoverColor: 'hover:bg-cyan-50 hover:text-cyan-700',
      hidden: !props.onComment,
      shortcut: 'Ctrl+B → M'
    },
    {
      id: 'attach',
      label: 'Anexar',
      icon: <HiPaperClip className="w-5 h-5" />,
      onClick: props.onAttach,
      color: 'text-violet-600',
      hoverColor: 'hover:bg-violet-50 hover:text-violet-700',
      hidden: !props.onAttach,
      shortcut: 'Ctrl+B → T'
    },
    {
      id: 'approve',
      label: 'Aprovar Autorização',
      icon: <HiCheck className="w-5 h-5" />,
      onClick: props.onApprove,
      color: 'text-green-600',
      hoverColor: 'hover:bg-green-50 hover:text-green-700',
      hidden: !props.onApprove,
      shortcut: 'Ctrl+B → Y'
    },
    {
      id: 'reprove',
      label: 'Reprovar Autorização',
      icon: <HiXMark className="w-5 h-5" />,
      onClick: props.onReprove,
      color: 'text-red-600',
      hoverColor: 'hover:bg-red-50 hover:text-red-700',
      hidden: !props.onReprove,
      shortcut: 'Ctrl+B → Z'
    }
  ],
  
  user: (props: FloatingToolbarProps) => [
    {
      id: 'edit',
      label: 'Editar Perfil',
      icon: <HiPencil className="w-5 h-5" />,
      onClick: props.onEdit,
      color: 'text-blue-600',
      hoverColor: 'hover:bg-blue-50 hover:text-blue-700'
    }
  ],
  
  admin: (props: FloatingToolbarProps) => [
    {
      id: 'edit',
      label: 'Gerenciar',
      icon: <HiPencil className="w-5 h-5" />,
      onClick: props.onEdit,
      color: 'text-red-600',
      hoverColor: 'hover:bg-red-50 hover:text-red-700'
    }
  ],
  
  general: (props: FloatingToolbarProps) => [
    {
      id: 'edit',
      label: 'Editar',
      icon: <HiPencil className="w-5 h-5" />,
      onClick: props.onEdit,
      color: 'text-gray-600',
      hoverColor: 'hover:bg-gray-50 hover:text-gray-700'
    }
  ]
};

export const FloatingToolbar: React.FC<FloatingToolbarProps> = ({
  // Ações
  actions,
  onEdit,
  onReturn,
  onResolve,
  onAssignToSelf,
  onSendReminder,
  
  // Novas ações para demandas
  onAssignTo,
  onDeliver,
  onUndoDelivery,
  onAccept,
  onAcceptDelivery,
  onReject,
  onDelete,
  onAddObservers,
  onTransferSector,
  onWrongDemand,
  onresend,
  onComment,
  onAttach,
  onRemoveAssigned,
  onRelatedDemand,
  
  // Ações de Autorização
  onApprove,
  onReprove,
  
  // Comportamentos
  autoCollapse = true,
  autoCollapseDelay = 1000,
  scrollCollapse = true,
  closeOnAction = true,
  
  // Aparência
  position = 'bottom-right',
  size = 'md',
  
  // Contexto
  context = 'demand',
  title = 'Ações',
  
  // Posicionamento
  relative = false,
  
  className = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isHovering, setIsHovering] = useState(false);
  const [autoCollapseTimer, setAutoCollapseTimer] = useState<number | null>(null);
  const [viewportDimensions, setViewportDimensions] = useState({ width: window.innerWidth, height: window.innerHeight });
  const [scrollPosition, setScrollPosition] = useState({ top: 0, canScrollUp: false, canScrollDown: false });
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef<number | null>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const glassScrollContainerRef = useRef<HTMLDivElement>(null);
  const modalManager = useModalManager();
  const minimizedModals = modalManager.minimizedModals || [];
  const restoreModal = modalManager.restoreModal;
  const getMinimizedModalByActionId = modalManager.getMinimizedModalByActionId;

  // Obter ações baseadas nas props
  const getActions = (): ToolbarAction[] => {
    if (actions) {
      return actions.filter(action => !action.hidden);
    }
    
    const contextActionsArray = contextActions[context]({
      onEdit, 
      onReturn, 
      onResolve, 
      onAssignToSelf, 
      onSendReminder,
      onAssignTo,
      onDeliver,
      onUndoDelivery,
      onAccept,
      onAcceptDelivery,
      onReject,
      onDelete,
      onAddObservers,
      onTransferSector,
      onWrongDemand,
      onresend,
      onComment,
      onAttach,
      onRemoveAssigned,
      onRelatedDemand,
      onApprove,
      onReprove
    } as FloatingToolbarProps);
    
    return contextActionsArray.filter((action: ToolbarAction) => !action.hidden);
  };

  const toolbarActions = getActions();
  const hasMinimizedModals = minimizedModals.length > 0;
  

  // Force collapsed when there are minimized modals
  useEffect(() => {
    if (hasMinimizedModals) {
      setIsExpanded(false);
    }
  }, [hasMinimizedModals]);

  // Funções para obter classes CSS baseadas nas props
  const getPositionClasses = (): string => {
    switch (position) {
      case 'bottom-right': return 'right-6 bottom-6';
      case 'bottom-left': return 'left-6 bottom-6';
      case 'top-right': return 'right-6 top-6';
      case 'top-left': return 'left-6 top-6';
      default: return 'right-6 bottom-6';
    }
  };

   const getSizeConfig = (): {
     collapsed: { width: string; height: string };
     expanded: { width: string[]; height: string[]; needsScrolling?: boolean };
     borderRadius: string;
     needsScrolling: boolean;
   } => {
     const actionCount = toolbarActions.length;
     
     // Get current viewport dimensions from state
     const viewportHeight = viewportDimensions.height;
     const viewportWidth = viewportDimensions.width;
     
     // Calculate dynamic height based on number of actions and viewport
     const actionHeight = 52; // Each action needs approximately 52px
     const headerHeight = 60; // Space for title
     const padding = 48; // Top and bottom padding
     const minHeight = 120;
     
     // Responsive max height based on viewport size
     let maxHeightRatio = 0.8;
     if (viewportHeight < 600) maxHeightRatio = 0.9; // Smaller screens get more space
     if (viewportHeight < 500) maxHeightRatio = 0.95; // Very small screens get almost full height
     
     const maxHeight = viewportHeight * maxHeightRatio;
     
     // Calculate ideal height
     const idealHeight = actionCount * actionHeight + headerHeight + padding;
     
     // Determine if we need scrolling
     const needsScrolling = idealHeight > maxHeight;
     const finalHeight = needsScrolling ? maxHeight : Math.max(idealHeight, minHeight);
     
     // Responsive width based on viewport
     let responsiveWidth = '280px';
     if (viewportWidth < 400) responsiveWidth = '260px'; // Narrow screens
     if (viewportWidth < 350) responsiveWidth = '240px'; // Very narrow screens
     
     switch (size) {
       case 'sm': return {
         collapsed: { width: '48px', height: '48px' },
         expanded: { 
           width: ['48px', '120px', '240px', viewportWidth < 350 ? '240px' : '260px'], 
           height: ['48px', '120px', `${finalHeight * 0.8}px`, `${finalHeight}px`],
           needsScrolling
         },
         borderRadius: '24px',
         needsScrolling
       };
       case 'lg': return {
         collapsed: { width: '80px', height: '80px' },
         expanded: { 
           width: ['80px', '160px', '340px', viewportWidth < 400 ? '320px' : '360px'], 
           height: ['80px', '160px', `${finalHeight * 1.1}px`, `${finalHeight * 1.2}px`],
           needsScrolling
         },
         borderRadius: '40px',
         needsScrolling
       };
       default: return {
         collapsed: { width: '64px', height: '64px' },
         expanded: { 
           width: ['64px', '140px', responsiveWidth, responsiveWidth], 
           height: ['64px', '140px', `${finalHeight}px`, `${finalHeight}px`],
           needsScrolling
         },
         borderRadius: '32px',
         needsScrolling
       };
     }
   };

  // Auto-collapse effect
  useEffect(() => {
    if (!autoCollapse) return;
    
    // Don't auto-collapse if there are minimized modals
    if (isExpanded && !isHovering && !hasMinimizedModals) {
      const timer = window.setTimeout(() => {
        setIsExpanded(false);
      }, autoCollapseDelay);
      setAutoCollapseTimer(timer);
    }

    return () => {
      if (autoCollapseTimer) {
        window.clearTimeout(autoCollapseTimer);
      }
    };
  }, [isExpanded, isHovering, autoCollapse, autoCollapseDelay, hasMinimizedModals]);

   // Scroll collapse effect
   useEffect(() => {
     if (!scrollCollapse) return;
     
     let lastScrollY = window.scrollY;
     
     const handleScroll = () => {
       const currentScrollY = window.scrollY;
       
       // Don't collapse on scroll if there are minimized modals
       if (currentScrollY > 100 && currentScrollY > lastScrollY && !hasMinimizedModals) {
         // Scrolling down
           setIsExpanded(false);
       }
       
       lastScrollY = currentScrollY;
     };
 
     window.addEventListener('scroll', handleScroll, { passive: true });
     return () => window.removeEventListener('scroll', handleScroll);
   }, [scrollCollapse, hasMinimizedModals]);

   // Viewport resize effect (handles zoom and window resize)
   useEffect(() => {
     const handleResize = () => {
       setViewportDimensions({ 
         width: window.innerWidth, 
         height: window.innerHeight 
       });
     };

   window.addEventListener('resize', handleResize, { passive: true });
   return () => window.removeEventListener('resize', handleResize);
 }, []);

// Handle arrow scroll
const handleArrowScroll = (direction: 'up' | 'down') => {
  const container = glassScrollContainerRef.current;
  if (!container) return;

  const scrollAmount = 120; // Scroll by ~2-3 action items
  const targetScroll = direction === 'up' 
    ? Math.max(0, container.scrollTop - scrollAmount)
    : Math.min(container.scrollHeight - container.clientHeight, container.scrollTop + scrollAmount);

  container.scrollTo({ top: targetScroll, behavior: 'smooth' });
};

  const handleMouseEnter = () => {
    setIsHovering(true);
    // Only auto-expand if there are no minimized modals
    if (!hasMinimizedModals) {
      setIsExpanded(true);
    }
    if (autoCollapseTimer) {
      window.clearTimeout(autoCollapseTimer);
    }
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    // Will trigger auto-collapse in useEffect
  };

  const handleActionClick = (action: ToolbarAction, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (action.disabled) return;
    
    if (action.onClick) {
      action.onClick();
    }
    
    // Fechar automaticamente após clicar se configurado
    if (closeOnAction) {
      setIsExpanded(false);
    }
  };

   const sizeConfig = getSizeConfig();
   const needsScrolling = sizeConfig.needsScrolling;

  // Scroll position tracking
  const updateScrollState = useCallback(() => {
    const container = glassScrollContainerRef.current;
    if (!container) {
      setScrollPosition({ top: 0, canScrollUp: false, canScrollDown: false });
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = container;
    const hasVerticalScrollbar = scrollHeight > clientHeight;
    const canScrollUp = scrollTop > 0;
    const canScrollDown = hasVerticalScrollbar && scrollTop < scrollHeight - clientHeight - 1;

    setScrollPosition({ top: scrollTop, canScrollUp, canScrollDown });
  }, []);

   // Update scroll state when needsScrolling changes
   useEffect(() => {
     updateScrollState();
   }, [needsScrolling, toolbarActions.length, isExpanded, updateScrollState]);

   // Update scroll state when toolbar becomes visible or container size changes
   useEffect(() => {
     if (isExpanded && !hasMinimizedModals) {
       // Delay to ensure container is fully rendered
       const timer = setTimeout(updateScrollState, 100);
       return () => clearTimeout(timer);
     }
     return undefined;
   }, [isExpanded, hasMinimizedModals, updateScrollState]);

   // Update scroll state when viewport changes
   useEffect(() => {
     if (isExpanded && !hasMinimizedModals) {
       const timer = setTimeout(updateScrollState, 50);
       return () => clearTimeout(timer);
     }
     return undefined;
   }, [viewportDimensions, isExpanded, hasMinimizedModals, updateScrollState]);

   // ResizeObserver to detect container size changes
   useEffect(() => {
     const container = glassScrollContainerRef.current;
     if (!container) return;

     const resizeObserver = new ResizeObserver(() => {
       updateScrollState();
     });

     const mutationObserver = new MutationObserver(() => {
       updateScrollState();
     });

     resizeObserver.observe(container);
     mutationObserver.observe(container, { childList: true, subtree: true });
     
     return () => {
       resizeObserver.disconnect();
       mutationObserver.disconnect();
     };
   }, [isExpanded, updateScrollState]);

   // Cleanup scroll timeout on unmount
   useEffect(() => {
     return () => {
       if (scrollTimeoutRef.current) {
         clearTimeout(scrollTimeoutRef.current);
       }
     };
   }, []);

  return (
    <>
      {/* Scrollbar styles */}
      <style>
        {`
          .scrollbar-none::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-none {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}
      </style>
      

       {/* Floating Toolbar */}
        <motion.div
          ref={toolbarRef}
          data-toolbar="floating"
          className={`${relative ? 'absolute' : 'fixed'} ${getPositionClasses()} z-[9999] select-none ${className}`}
          style={{
            transformOrigin: position.includes('bottom') ? 
              (position.includes('right') ? 'bottom right' : 'bottom left') :
              (position.includes('right') ? 'top right' : 'top left'),
          }}
         initial={{ opacity: 0, scale: 0.8, x: 20, y: 20 }}
         animate={{ 
           opacity: [0, 0.3, 0.7, 1], 
           scale: [0.8, 0.9, 1.05, 1],
           x: [20, 10, -2, 0],
           y: [20, 10, -2, 0]
         }}
        transition={{ 
          duration: 0.15,
          ease: [0.16, 1, 0.3, 1],
          times: [0, 0.3, 0.7, 1]
        }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      >
        {/* Simplified Container */}
        <motion.div 
          className="relative flex font-semibold overflow-hidden cursor-pointer transition-all duration-150 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md rounded-3xl border border-white/20 dark:border-gray-700/50"
          onClick={(e) => {
            if (hasMinimizedModals) {
              // When minimized modal exists, clicking restores it - NO expansion
              e.stopPropagation();
              restoreModal(minimizedModals[0].id);
              return; // Exit early to prevent any other logic
            } else if (!isExpanded) {
              // Normal behavior: expand toolbar
              e.stopPropagation();
              setIsExpanded(true);
            }
          }}
           animate={{
             width: hasMinimizedModals 
               ? 56 
               : (isExpanded 
                 ? sizeConfig.expanded.width
                 : [sizeConfig.expanded.width[3], sizeConfig.expanded.width[2], sizeConfig.expanded.width[1], sizeConfig.collapsed.width]),
             height: hasMinimizedModals 
               ? 56 
               : (isExpanded 
                 ? sizeConfig.expanded.height
                 : [sizeConfig.expanded.height[3], sizeConfig.expanded.height[2], sizeConfig.expanded.height[1], sizeConfig.collapsed.height]),
              borderRadius: sizeConfig.borderRadius,
              scale: isExpanded 
                ? [1, 1.02, 0.98, 1] 
                : [1, 0.98, 1.02, 1]
            }}
      transition={{ 
            duration: 0.25,
            ease: [0.16, 1, 0.3, 1],
            times: [0, 0.3, 0.7, 1]
          }}
        style={{
            willChange: "transform, width, height",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)",
          }}
        >

          {/* Content Container */}
          <div className="relative z-40 w-full h-full flex items-center justify-center">
            {hasMinimizedModals ? (
              // Show minimized modal content inside the circular toolbar
              <motion.div
                key="minimized-content"
                className="flex items-center justify-center w-full h-full relative pointer-events-none"
                style={{ willChange: "transform, opacity" }}
                title={`Restaurar: ${minimizedModals[0].title}`}
              >
                <motion.div className="relative z-10 text-blue-600 dark:text-blue-400">
                  {minimizedModals[0].icon}
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                </motion.div>
              </motion.div>
            ) : (
              <AnimatePresence mode="wait">
                {isExpanded ? (
                <motion.div
                  key="expanded"
                      initial={{ 
                        opacity: 0, 
                      scale: 0.7,
                        y: 20,
                      rotateX: -15
                      }}
                      animate={{ 
                      opacity: [0, 0.3, 0.8, 1], 
                      scale: [0.7, 0.9, 1.02, 1],
                      y: [20, 10, -2, 0],
                      rotateX: [-15, -8, 2, 0]
                      }}
                      exit={{ 
                      opacity: [1, 0.7, 0.3, 0], 
                      scale: [1, 0.95, 0.8, 0.7],
                      y: [0, -5, 10, 20],
                      rotateX: [0, 5, -10, -15]
                      }}
                      transition={{ 
                      duration: 0.12, 
                      ease: [0.16, 1, 0.3, 1],
                      times: [0, 0.3, 0.7, 1]
                    }}
                    className="flex flex-col items-center w-full h-full min-h-0 p-6"
                  >
                   <div className="flex items-center justify-between mb-2">
                     <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                       {title}
                     </h3>
                   </div>
                   
                   {/* Scroll up arrow */}
                   {!hasMinimizedModals && needsScrolling && scrollPosition.canScrollUp && (
                     <motion.button
                       initial={{ opacity: 0, y: -5 }}
                       animate={{ opacity: 1, y: 0 }}
                       exit={{ opacity: 0, y: -5 }}
                       onClick={() => handleArrowScroll('up')}
                       className="flex items-center justify-center w-full py-1 mb-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100/50 dark:hover:bg-gray-700/30 rounded transition-all duration-150"
                       title="Rolar para cima"
                     >
                       <HiChevronUp className="w-3 h-3" />
                     </motion.button>
                   )}
                   
                   {!hasMinimizedModals && (
                  <div className={`w-full flex-1 min-h-0 relative ${needsScrolling ? 'overflow-y-auto scrollbar-none' : 'overflow-hidden'}`}
                       ref={(el) => {
                         glassScrollContainerRef.current = el;
                         // Update scroll state when container is first set
                         if (el) {
                           setTimeout(updateScrollState, 10);
                         }
                       }}
                       onScroll={() => {
                         updateScrollState();
                         
                         // Track scrolling state
                         setIsScrolling(true);
                         
                         // Clear existing timeout
                         if (scrollTimeoutRef.current) {
                           clearTimeout(scrollTimeoutRef.current);
                         }
                         
                         // Re-enable glass effect after scroll ends
                         scrollTimeoutRef.current = window.setTimeout(() => {
                           setIsScrolling(false);
                         }, 80); // Balanced delay for smooth scroll + quick reappearance
                       }}
                       style={{ 
                         scrollbarWidth: 'none', 
                         msOverflowStyle: 'none'
                       }}
                  >
                    <GlassHoverEffect
                      direction="vertical"
                      className="flex flex-col space-y-2 w-full"
                      glassClassName={`bg-blue-50/50 dark:bg-blue-900/20 border-blue-200/30 dark:border-blue-700/30 ${
                        isScrolling ? 'opacity-0 pointer-events-none' : 'opacity-100'
                      } transition-opacity duration-150 ease-out`}
                    >
                      {({ containerRef, containerProps, getItemProps }) => (
                        <div 
                          ref={containerRef as React.RefObject<HTMLDivElement>}
                          {...containerProps}
                          className="flex flex-col space-y-2 w-full"
                        >
                        {toolbarActions.map((action, index) => {
                          const minimizedModal = getMinimizedModalByActionId(action.id);
                          const isMinimizedAction = !!minimizedModal;
                          const isDisabled = hasMinimizedModals && !isMinimizedAction;
                          
                          // Build tooltip text
                          const tooltipText = action.shortcut && !isMinimizedAction && !isDisabled
                            ? `${action.label} (${action.shortcut})`
                            : action.label;
                          
                          return (
                            <motion.button
                              key={action.id}
                              {...getItemProps(index)}
                              title={tooltipText}
                              initial={{ 
                                opacity: 0, 
                                x: -30,
                                y: 10,
                                scale: 0.8,
                                rotateY: -15
                              }}
                              animate={{ 
                                opacity: isDisabled ? [0, 0.2, 0.4, 0.3] : [0, 0.4, 0.8, 1], 
                                x: [-30, -15, 3, 0],
                                y: [10, 5, -1, 0],
                                scale: [0.8, 0.9, 1.02, 1],
                                rotateY: [-15, -8, 2, 0]
                              }}
                              transition={{ 
                                delay: index * 0.025,
                                duration: 0.15,
                                ease: [0.16, 1, 0.3, 1],
                                times: [0, 0.3, 0.7, 1]
                              }}
                              disabled={isDisabled}
                              onClick={(e) => {
                                e.stopPropagation(); // Always stop propagation to prevent toolbar toggle
                                if (isDisabled) return;
                                
                                if (isMinimizedAction) {
                                  restoreModal(minimizedModal.id);
                                } else {
                                  // For regular actions, only if no minimized modals exist
                                  if (!hasMinimizedModals) {
                                    // Expand toolbar first if collapsed
                                    if (!isExpanded) {
                                      setIsExpanded(true);
                                    }
                                    handleActionClick(action, e);
                                  }
                                }
                              }}
                              className={`
                                flex items-center space-x-3 w-full px-4 py-3 rounded-2xl 
                                transition-all duration-200 ease-out relative z-10
                                ${isDisabled 
                                  ? 'text-gray-400 cursor-not-allowed opacity-50'
                                  : isMinimizedAction 
                                    ? 'text-blue-600 hover:text-blue-700' 
                                    : 'text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100'
                                }
                                min-w-0
                              `}
                              style={{ willChange: "transform, opacity" }}
                            >
                              <motion.div
                                className={`${isMinimizedAction ? 'text-blue-600' : action.color} transition-all duration-200 ease-out flex-shrink-0 relative`}
                              >
                                {isMinimizedAction ? minimizedModal.icon : action.icon}
                                {isMinimizedAction && (
                                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                                )}
                              </motion.div>
                              <span className="text-sm font-medium truncate flex-1 text-left transition-all duration-200 ease-out relative min-w-0">
                                {isMinimizedAction ? `${minimizedModal.title} (minimizado)` : action.label}
                              </span>
                            </motion.button>
                          );
                        })}
                      </div>
                     )}
                   </GlassHoverEffect>
                   </div>
                   )}
                   
                   {/* Scroll down arrow */}
                   {!hasMinimizedModals && needsScrolling && scrollPosition.canScrollDown && (
                     <motion.button
                       initial={{ opacity: 0, y: 5 }}
                       animate={{ opacity: 1, y: 0 }}
                       exit={{ opacity: 0, y: 5 }}
                       onClick={() => handleArrowScroll('down')}
                       className="flex items-center justify-center w-full py-1 mt-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100/50 dark:hover:bg-gray-700/30 rounded transition-all duration-150"
                       title="Rolar para baixo"
                     >
                       <HiChevronDown className="w-3 h-3" />
                     </motion.button>
                   )}
                </motion.div>
              ) : (
                <motion.div
                  key="collapsed"
                  initial={{ 
                    opacity: 0, 
                    scale: 0.3,
                    rotate: -90,
                    y: 10
                  }}
                  animate={{ 
                    opacity: [0, 0.4, 0.8, 1], 
                    scale: [0.3, 0.7, 1.1, 1],
                    rotate: [-90, -45, 15, 0],
                    y: [10, 5, -2, 0]
                  }}
                  exit={{ 
                    opacity: [1, 0.7, 0.3, 0], 
                    scale: [1, 0.8, 0.5, 0.3],
                    rotate: [0, 30, -60, -90],
                    y: [0, -3, 5, 10]
                  }}
                  transition={{ 
                    duration: 0.12, 
                    ease: [0.16, 1, 0.3, 1],
                    times: [0, 0.3, 0.7, 1]
                  }}
                   className="flex items-center justify-center w-full h-full"
                 >
                   <div className="text-yellow-600 dark:text-yellow-500">
                     <HiBolt className="w-6 h-6" />
                   </div>
              </motion.div>
            )}
          </AnimatePresence>
            )}
        </div>

            </motion.div>
    </motion.div>
    </>
  );
};

// Função utilitária para criar ações customizadas
export const createToolbarAction = (
  id: string,
  label: string,
  icon: React.ReactNode,
  onClick?: () => void,
  options?: {
    color?: string;
    hoverColor?: string;
    hidden?: boolean;
    disabled?: boolean;
  }
): ToolbarAction => ({
  id,
  label,
  icon,
  onClick,
  color: options?.color || 'text-gray-600',
  hoverColor: options?.hoverColor || 'hover:bg-gray-50 hover:text-gray-700',
  hidden: options?.hidden || false,
  disabled: options?.disabled || false,
});

// Componentes pré-configurados para diferentes contextos
export const DemandToolbar: React.FC<Omit<FloatingToolbarProps, 'context'>> = (props) => (
  <FloatingToolbar {...props} context="demand" />
);

export const UserToolbar: React.FC<Omit<FloatingToolbarProps, 'context'>> = (props) => (
  <FloatingToolbar {...props} context="user" />
);

export const AdminToolbar: React.FC<Omit<FloatingToolbarProps, 'context'>> = (props) => (
  <FloatingToolbar {...props} context="admin" />
);

export const GeneralToolbar: React.FC<Omit<FloatingToolbarProps, 'context'>> = (props) => (
  <FloatingToolbar {...props} context="general" />
);

// Exportar tipos para uso externo
export type { FloatingToolbarProps, ToolbarAction };

export default FloatingToolbar;
